import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { IAncillaryRule, SimulateAncillaryResult } from '@carrierbridge/shared';
import api from '@/lib/api';

const KEY = ['ancillary-rules'] as const;

export function useAncillaryRules(params?: {
  carrierId?: string;
  active?: boolean;
  sourceType?: string;
  status?: string;
  timing?: string;
}, options?: { enabled?: boolean }) {
  return useQuery<IAncillaryRule[]>({
    queryKey: [...KEY, params],
    queryFn: async () => {
      const { data } = await api.get<IAncillaryRule[]>('/ancillary-rules', { params });
      return data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useCarrierAncillaryRules(carrierId: string | undefined) {
  return useQuery<IAncillaryRule[]>({
    queryKey: [...KEY, 'carrier', carrierId],
    queryFn: async () => {
      const { data } = await api.get<IAncillaryRule[]>(
        `/carriers/${carrierId}/ancillary-rules`,
      );
      return data;
    },
    enabled: !!carrierId,
  });
}

export function useProposalAncillaryRules(proposalId: string | undefined) {
  return useQuery<IAncillaryRule[]>({
    queryKey: [...KEY, 'proposal', proposalId],
    queryFn: async () => {
      const { data } = await api.get<IAncillaryRule[]>(
        `/tender-proposals/${proposalId}/ancillary-rules`,
      );
      return data;
    },
    enabled: !!proposalId,
  });
}

export function useCreateProposalAncillaryRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, dto }: { proposalId: string; dto: any }) => {
      const { data } = await api.post<IAncillaryRule>(
        `/tender-proposals/${proposalId}/ancillary-rules`,
        dto,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAncillaryRule(id: string | undefined) {
  return useQuery<IAncillaryRule>({
    queryKey: [...KEY, id],
    queryFn: async () => {
      const { data } = await api.get<IAncillaryRule>(`/ancillary-rules/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateAncillaryRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ carrierId, dto }: { carrierId: string; dto: any }) => {
      const { data } = await api.post<IAncillaryRule>(
        `/carriers/${carrierId}/ancillary-rules`,
        dto,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateAncillaryRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: any }) => {
      const { data } = await api.put<IAncillaryRule>(`/ancillary-rules/${id}`, dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteAncillaryRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/ancillary-rules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useBulkDeleteAncillaryRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => api.delete(`/ancillary-rules/${id}`)));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface OptionCatalogItem {
  key: string;
  label: string;
  group: string | null;
  defaultEnabled: boolean;
}

/**
 * Fetches all active QUOTE rules and builds the option catalog.
 * Available immediately on page load, no need to wait for pricing results.
 */
export function useOptionCatalog() {
  const { data: rules = [] } = useAncillaryRules({
    active: true,
    status: 'ACTIVE',
    timing: 'QUOTE',
  });

  return useMemo(() => {
    const catalog: OptionCatalogItem[] = [];
    const seen = new Set<string>();

    for (const rule of rules) {
      const key = (rule as any).optionKey;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      catalog.push({
        key,
        label: (rule as any).optionLabel || key,
        group: (rule as any).optionGroup || null,
        defaultEnabled: (rule as any).optionDefaultEnabled || false,
      });
    }

    catalog.sort((a, b) => {
      const ga = a.group || '';
      const gb = b.group || '';
      if (ga !== gb) return ga.localeCompare(gb);
      return a.label.localeCompare(b.label);
    });

    return catalog;
  }, [rules]);
}

export function useSimulateAncillaryRule() {
  return useMutation({
    mutationFn: async (dto: { ruleId?: string; rule?: any; shipmentInputs: any }) => {
      const { data } = await api.post<SimulateAncillaryResult>(
        '/ancillary-rules/simulate',
        dto,
      );
      return data;
    },
  });
}

export interface AiCompleteResult {
  name: string;
  description?: string | null;
  status: string;
  timing: string;
  priority: number;
  optionKey?: string | null;
  optionLabel?: string | null;
  optionGroup?: string | null;
  optionDefaultEnabled?: boolean;
  calculation?: {
    calc_type: string;
    base: string;
    amount?: number | null;
    rate?: number | null;
    per_unit?: string | null;
    min_charge?: number | null;
    max_charge?: number | null;
  } | null;
  conditionsJson?: Record<string, any>;
}

export function useAiCompleteAncillaryRule() {
  return useMutation({
    mutationFn: async (description: string) => {
      const { data } = await api.post<AiCompleteResult>(
        '/ancillary-rules/ai-complete',
        { description },
      );
      return data;
    },
  });
}
